import { Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  pickTheFieldsFrom,
  CONFIG_KEYS,
  filterDifferences,
} from "../utils";
import { useTranslation } from "react-i18next";
import { updateMerchant, updateMerchantOtherInfo } from "../model";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import { useSnackbar } from "notistack";
import ConfigSection from "../../add-offline-merchant/Component/ConfigSection";
import useFormUndo from "utils/useFormUndo/useFormUndo";
import { formPreventEnterSubmit } from "utils/utils";
import usePageWrapper from "utils/usePageWrapper/usePageWrapper";
import { FormBuilder } from "components/FormBuilder/FormBuilder";

const PAGE_LOCALE_KEY = "page.merchants.";

/**
 * @param {Object} props
 * @param {import("utils/usePageWrapper/usePageWrapper").CallApisFn<import("../OfflineMerchantDetail").offlineMerchantDetailPartitionKeyType>} props.callApis 
 * @returns 
 */
const MerchantConfig = ({
  callApis,
  merchant,
  merchantOtherDetail,
  fetchMerchantAndMerchantOtherDetail,
  staticMerchantPayload,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const formProps = useForm({
    mode: "onChange",
  });
  const { handleSubmit, reset } = formProps;
  const { rememberForm, undoForm, clearFormMemory } = useFormUndo({ formProps });
  const { PageStickyFooter } = usePageWrapper();

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const onSubmit = async (formValue) => {
    try {
      setIsLoading(true);

      const MERCHANT_UPDATE_IGNORE_FIELDS = [
        "settleCycle",
        "autoApproved",
        "settleMode",
        "batchAnchorDate",
        "batchDelayDay",
        "batchInterval",
        "batchUnit",
        "otherInfo",
        "zeroPlan",
      ];
      const MERCHANT_OTHER_INFO_UPDATE_FIELDS = [
        "settleCycle",
        "autoApproved",
        "zeroPlan",
      ];
      const MERCHANT_OTHER_INFO_UPDATE_IGNORE_FIELDS = ["otherInfo"];

      const apiConfigs = [
        {
          key: "MERCHANT",
          diff: filterDifferences(
            merchant,
            formValue,
            [],
            MERCHANT_UPDATE_IGNORE_FIELDS,
          ),
          payload: {
            ...staticMerchantPayload(merchant, "config", {
              previewUrl: formValue.merchantLogo,
            }),
            ...filterDifferences(
              merchant,
              formValue,
              [],
              MERCHANT_UPDATE_IGNORE_FIELDS,
            ),
          },
          apiCall: updateMerchant,
        },
        {
          key: "MERCHANT_OTHER_INFO",
          diff: filterDifferences(
            merchantOtherDetail,
            formValue,
            MERCHANT_OTHER_INFO_UPDATE_FIELDS,
            MERCHANT_OTHER_INFO_UPDATE_IGNORE_FIELDS,
          ),
          payload: {
            ...filterDifferences(
              merchantOtherDetail,
              formValue,
              MERCHANT_OTHER_INFO_UPDATE_FIELDS,
              MERCHANT_OTHER_INFO_UPDATE_IGNORE_FIELDS,
            ),
            merchantId: merchant?.merchantId,
            id: merchantOtherDetail?.id,
            ...(merchantOtherDetail?.sales?.id && {
              sales: { id: merchantOtherDetail.sales.id },
            }),
            ...(merchantOtherDetail?.onboardSales?.id && {
              onboardSales: { id: merchantOtherDetail.onboardSales.id },
            }),
            ...(merchantOtherDetail?.referrer?.id && {
              referrer: { id: merchantOtherDetail.referrer.id },
            }),
          },
          apiCall: updateMerchantOtherInfo,
        },
      ];
      const validApis = apiConfigs.filter((config) => {
        console.log(`${config.key} config.diff`, config.diff);
        return Object.keys(config.diff).length > 0;
      });
      if (validApis.length === 0) {
        snackbar(
          t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.noChanges"),
          "info",
        );
        return;
      }

      const [...resps] = await callApis([
        ...validApis.map((config) => ({
          api: config.apiCall,
          payload: config.payload,
          blockOnError: false,
          blockOnUnauthorized: false,
        })),
      ]);

      let isAllSuccess = true;
      let isSomeError = false;

      resps.forEach((resp) => {
        !resp.isSuccess && (isAllSuccess = false);
        resp.isError && (isSomeError = true);
      })

      if (isAllSuccess && validApis.length > 0) {
        snackbar(
          t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.updateSuccess"),
          "success",
        );
        await fetchMerchantAndMerchantOtherDetail();
        setIsEditing(false);
        clearFormMemory();
      } else {
        isSomeError && snackbar(
          t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.updateFailed"),
          "error",
        );
      }
    } catch (error) {
      console.error("Submission error:", error);
      snackbar(
        `${t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.updateFailed")}: ${error.message}`,
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };
  const appendBase64Data = (base64String) => {
    if (base64String?.startsWith("iVBORw0KGgo")) {
      base64String = "data:image/png;base64," + base64String;
    }
    if (base64String?.startsWith("/9j/")) {
      base64String = "data:image/jpeg;base64," + base64String;
    }
    return base64String;
  };

  useEffect(() => {
    if (Object.keys(merchant).length > 0) {
      // console.log("merchant", merchant);
      const initialValue = {
        ...pickTheFieldsFrom(merchant, CONFIG_KEYS()),
        ...pickTheFieldsFrom(merchantOtherDetail, CONFIG_KEYS()),
      };
      setAllValues(initialValue);
    }
  }, [merchant]);

  const setAllValues = (initialValue) => {
    reset(
      {
        ...initialValue,
        autoApproved: initialValue?.autoApproved ?? true,
        isKDollar: !!initialValue?.otherInfo?.campaign?.find(
          (campaign) => campaign === "KDOLLAR",
        ),
        merchantLogo: appendBase64Data(initialValue?.merchantLogo),
      },
      {
        keepDefaultValues: false,
        keepDirty: false,
        keepErrors: false,
      },
    );
  };

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
      <FormBuilder.Section>
        <ConfigSection
          formProps={formProps}
          isEdit={isEditing}
          isLoading={isLoading}
          showSettlementMode
        />
        <PageStickyFooter>
          <Stack
            direction={"row"}
            gap={2}
            justifyContent={"space-between"}
          >
            <div></div>
            <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
              {!isEditing ? (
                <ButtonMAS
                  variant="primary"
                  onClick={() => {
                    setIsEditing(true);
                    rememberForm()
                  }}
                  disabled={isLoading}
                >
                  {t(`${PAGE_LOCALE_KEY}merchantDetail.button.edit`)}
                </ButtonMAS>
              ) : (
                <>
                  <ButtonMAS
                    variant="bordered"
                    onClick={() => {
                      setIsEditing(false);
                      undoForm();
                    }}
                    disabled={isLoading}
                  >
                    {t(`${PAGE_LOCALE_KEY}merchantDetail.button.cancel`)}
                  </ButtonMAS>
                  <ButtonMAS
                    type="submit"
                    isLoading={isLoading}
                    disabled={isLoading}
                  >
                    {t(`${PAGE_LOCALE_KEY}merchantDetail.button.save`)}
                  </ButtonMAS>
                </>
              )}
            </Stack>
          </Stack>
        </PageStickyFooter>
      </FormBuilder.Section>
    </form>
  );
};

export default React.memo(MerchantConfig);
